import { isWeb } from '@gluestack-ui/nativewind-utils/IsWeb';
import { useQuery } from '@tanstack/react-query';
import { useLocalSearchParams, useRouter } from 'expo-router';
import Head from 'expo-router/head';
import { useEffect, useRef } from 'react';
import { ScrollView, View } from 'react-native';

import { SharedPostBody } from './SharedPostBody';
import { useSharedLibraryPost } from '../../hooks/useSharedLibraryPost';

import { Header } from '@/components/header';
import { Heading } from '@/components/heading';
import { Button, ButtonText } from '@/components/ui/button';
import { HStack } from '@/components/ui/hstack';
import { Spinner } from '@/components/ui/spinner';
import { Text } from '@/components/ui/text';
import { VStack } from '@/components/ui/vstack';
import { ROUTES } from '@/constants/routes';
import { useAuthStatus } from '@/hooks/useAuthStatus';
import { getAccessibilityProps } from '@/util/accessibilityProps';
import {
  trackCommunityPostViewed,
  trackCommunitySharedPostRedirected,
} from '@/util/analyticsEvents';

const JoinCta = () => {
  const router = useRouter();

  return (
    <VStack className="mx-5 mb-10 gap-3 rounded-xl border border-outline-100 bg-background-50 p-5">
      <Text className="font-body font-semibold text-base text-typography-900">
        Primum connects community oncologists with expert specialists.
      </Text>
      <Text size="sm" className="text-typography-700">
        Sign in or create an account to comment, save inquiries, and ask experts.
      </Text>
      <HStack className="gap-3">
        <Button
          action="primary"
          size="md"
          onPress={() => router.push(ROUTES.LOGIN)}
          {...getAccessibilityProps({ label: 'Sign in', role: 'button' })}>
          <ButtonText>Sign in</ButtonText>
        </Button>
      </HStack>
    </VStack>
  );
};

const UnavailableState = () => (
  <VStack
    className="flex-1 items-center justify-center gap-3 px-8 py-24"
    accessibilityLiveRegion="polite">
    <Heading size="h2-md" className="text-center">
      This shared link is no longer available
    </Heading>
    <Text className="text-center text-typography-600">
      The inquiry may have been unshared or removed. Ask whoever shared it for an updated link.
    </Text>
  </VStack>
);

/**
 * Static `noindex` head emitted on every render branch so shared posts are not
 * search-indexable regardless of load state. Lifted to module scope to avoid
 * recreating the element on each render.
 */
const NOINDEX_HEAD = (
  <Head>
    <meta name="robots" content="noindex" />
  </Head>
);

/**
 * Public, read-only viewer for a community library post resolved from a share
 * token (`/community/shared/{token}`).
 *
 * Renders without authentication for both anonymous visitors and signed-in
 * Anonymous visitors see the post read-only: title, cancer-type badge, published
 * date, author attribution, and body (split into answers behind INQUIRY_LIBRARY_ANSWERS when
 * the response carries a question, see `SharedPostBody`), plus a conversion
 * CTA — no comments, likes, saves, or prev/next navigation. Signed-in users are
 * redirected to the full authenticated post (`/community/{id}`) so they get the
 * complete experience; a `COMMUNITY_SHARED_POST_REDIRECTED` event fires here
 * before the redirect and the destination screen emits its own
 * `COMMUNITY_POST_VIEWED`. The `(open)` layout exempts this path from its generic
 * authenticated-user redirect so this targeted redirect can resolve the post id
 * first. A revoked or unknown token (404) renders a dedicated "unavailable" state,
 * and the page is `noindex` on web.
 */
export function PublicCommunityPost() {
  const { token, email } = useLocalSearchParams<{ token: string; email?: string }>();

  const router = useRouter();

  const { isAuthenticated } = useAuthStatus();

  const postQuery = useSharedLibraryPost(token);

  const handledPostIdRef = useRef<number | null>(null);

  useEffect(() => {
    const resolvedPost = postQuery.data;

    if (!resolvedPost || handledPostIdRef.current === resolvedPost.id) {
      return;
    }

    handledPostIdRef.current = resolvedPost.id;

    if (isAuthenticated) {
      trackCommunitySharedPostRedirected({
        postId: resolvedPost.id,
        email: email || undefined,
      });

      // Target the secure route directly so the (open) layout's authed-user
      // redirect doesn't re-fire and append the share token to the destination URL.
      router.replace(`/(secure)/(tabs)/(community)/community/${resolvedPost.id}`);

      return;
    }

    trackCommunityPostViewed({
      postId: resolvedPost.id,
      source: 'shared_link',
      email: email || undefined,
    });
  }, [postQuery.data, isAuthenticated, email, router]);

  // Signed-in users are mid-redirect to the authenticated post; hold the spinner
  // rather than flashing the teaser.
  const isRedirectingToFullPost = isAuthenticated && !!postQuery.data;

  if (!token || postQuery.isPending || isRedirectingToFullPost) {
    return (
      <>
        {NOINDEX_HEAD}
        <Header />
        <View
          className="py-24"
          accessibilityLiveRegion="polite"
          {...getAccessibilityProps({ label: 'Loading shared inquiry', role: 'progressbar' })}>
          <Spinner size="large" />
        </View>
      </>
    );
  }

  if (postQuery.isError || !postQuery.data) {
    return (
      <>
        {NOINDEX_HEAD}
        <Header />
        <UnavailableState />
      </>
    );
  }

  return (
    <>
      {NOINDEX_HEAD}
      <Header />
      <ScrollView
        showsVerticalScrollIndicator={false}
        contentContainerClassName="mx-auto w-full web:max-w-screen-md">
        <View className={isWeb ? '' : 'flex-1'}>
          <SharedPostBody post={postQuery.data} />
          <JoinCta />
        </View>
      </ScrollView>
    </>
  );
}
